import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { 
  TrendingUp, 
  ScrollText, 
  Landmark,
  BarChart3,
  PieChart, 
  ShieldAlert,
  Search
} from 'lucide-react';
import { Input } from '../components/ui/input';
import Layout from '../components/Layout';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const iconMap = {
  TrendingUp,
  ScrollText,
  Landmark,
  BarChart3,
  PieChart,
  ShieldAlert
};

export default function FAQs() {
  const { t, language } = useLanguage();
  const [faqs, setFaqs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [faqsRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/faqs`),
        axios.get(`${API}/faqs/categories`)
      ]);
      setFaqs(faqsRes.data);
      setCategories(categoriesRes.data.categories);
    } catch (error) {
      console.error('Failed to fetch FAQs:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredFaqs = faqs.filter(faq => {
    const matchesCategory = !selectedCategory || faq.category === selectedCategory;
    const query = searchQuery.toLowerCase();
    const matchesSearch = !searchQuery || 
      faq.question_en.toLowerCase().includes(query) ||
      faq.question_sw.toLowerCase().includes(query) ||
      faq.answer_en.toLowerCase().includes(query) ||
      faq.answer_sw.toLowerCase().includes(query);
    return matchesCategory && matchesSearch;
  });

  const getCategoryColor = (categoryId) => {
    const colors = {
      stocks: 'bg-blue-500',
      bonds: 'bg-green-500',
      treasury_bills: 'bg-purple-500',
      etfs: 'bg-orange-500',
      utt: 'bg-teal-500',
      risk: 'bg-red-500'
    };
    return colors[categoryId] || 'bg-primary';
  };

  return (
    <Layout>
      <div className="page-enter space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground mb-2">
            {t('Frequently Asked Questions', 'Maswali Yanayoulizwa Mara kwa Mara')}
          </h1>
          <p className="text-muted-foreground">
            {t('Learn about investment products in Tanzania', 'Jifunze kuhusu bidhaa za uwekezaji Tanzania')}
          </p>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
          <Input
            placeholder={t('Search questions...', 'Tafuta maswali...')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="h-12 pl-12 rounded-full bg-muted border-0"
            data-testid="faq-search-input"
          />
        </div>

        {/* Categories */}
        <div className="flex flex-wrap gap-2">
          <Button
            variant={selectedCategory === null ? "default" : "outline"}
            size="sm"
            onClick={() => setSelectedCategory(null)}
            className="rounded-full"
            data-testid="faq-category-all"
          >
            {t('All', 'Zote')}
          </Button>
          {categories.map(cat => {
            const Icon = iconMap[cat.icon] || TrendingUp;
            return (
              <Button
                key={cat.id}
                variant={selectedCategory === cat.id ? "default" : "outline"}
                size="sm"
                onClick={() => setSelectedCategory(cat.id)}
                className="rounded-full"
                data-testid={`faq-category-${cat.id}`}
              >
                <Icon className="w-4 h-4 mr-1" />
                {language === 'sw' ? cat.name_sw : cat.name_en}
              </Button>
            );
          })}
        </div>

        {/* FAQ List */}
        {loading ? (
          <div className="space-y-4">
            {[1,2,3].map(i => (
              <Card key={i} className="animate-pulse">
                <CardContent className="p-4">
                  <div className="h-6 bg-muted rounded w-3/4 mb-2" />
                  <div className="h-4 bg-muted rounded w-1/2" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : filteredFaqs.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="p-8 text-center">
              <Search className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">
                {t('No questions found. Try a different search.', 'Hakuna maswali yaliyopatikana. Jaribu utafutaji tofauti.')}
              </p>
            </CardContent>
          </Card>
        ) : (
          <Accordion type="single" collapsible className="space-y-3">
            {filteredFaqs.map((faq, index) => {
              const category = categories.find(c => c.id === faq.category);
              const Icon = category ? iconMap[category.icon] : TrendingUp;
              
              return (
                <AccordionItem 
                  key={faq.id} 
                  value={faq.id}
                  className="border border-border rounded-xl overflow-hidden bg-card faq-item stagger-item"
                  data-testid={`faq-item-${index}`}
                >
                  <AccordionTrigger className="px-4 py-4 hover:no-underline">
                    <div className="flex items-start gap-3 text-left">
                      <div className={`w-10 h-10 rounded-lg ${getCategoryColor(faq.category)} flex items-center justify-center flex-shrink-0`}>
                        <Icon className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="font-medium text-foreground">
                          {language === 'sw' ? faq.question_sw : faq.question_en}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {category && (language === 'sw' ? category.name_sw : category.name_en)}
                        </p>
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-4 pb-4">
                    <div className="pl-13 ml-13">
                      <p className="text-muted-foreground whitespace-pre-wrap leading-relaxed">
                        {language === 'sw' ? faq.answer_sw : faq.answer_en}
                      </p>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>
        )}

        {/* Info Card */}
        <Card className="bg-primary/5 border-primary/20">
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">
              {t(
                'For more information, visit CMSA (cmsa.go.tz), Bank of Tanzania (bot.go.tz), or UTT AMIS (uttamis.co.tz).',
                'Kwa maelezo zaidi, tembelea CMSA (cmsa.go.tz), Benki Kuu ya Tanzania (bot.go.tz), au UTT AMIS (uttamis.co.tz).'
              )}
            </p>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
